import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { 
  Calculator, 
  Briefcase, 
  Building2, 
  TrendingUp, 
  BookOpen, 
  FileWarning,
  Bot,
  Check,
  Users,
  Clock,
  ExternalLink,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSettings } from "@/context/SettingsContext";

const services = [
  {
    id: "individual",
    icon: Calculator,
    title: "Individual Tax Preparation",
    description: "Comprehensive tax preparation for individuals with various income sources.",
    included: [
      "W-2 wage income",
      "Investment income (dividends, capital gains)",
      "Rental property income",
      "Retirement distributions",
      "Education credits and deductions",
      "Itemized deduction analysis"
    ],
    whoItsFor: "Employees, retirees, investors, and individuals with multiple income sources.",
    turnaround: "5-10 business days (standard), 2-3 business days (express)"
  },
  {
    id: "self-employed",
    icon: Briefcase,
    title: "Self-Employed / 1099",
    description: "Specialized tax preparation for freelancers, contractors, and gig workers.",
    included: [
      "Schedule C preparation",
      "1099 income reporting",
      "Business expense deductions",
      "Home office deduction",
      "Self-employment tax calculation",
      "Quarterly estimate guidance"
    ],
    whoItsFor: "Freelancers, independent contractors, gig economy workers, and consultants.",
    turnaround: "7-14 business days"
  },
  {
    id: "business",
    icon: Building2,
    title: "Small Business Tax",
    description: "Complete business tax services for LLCs, S-Corps, and C-Corps.",
    included: [
      "LLC/Partnership returns (Form 1065)",
      "S-Corporation returns (Form 1120-S)",
      "C-Corporation returns (Form 1120)",
      "Multi-state filing support",
      "K-1 preparation and distribution",
      "Business deduction optimization"
    ],
    whoItsFor: "Small business owners, partnerships, and corporations.",
    turnaround: "10-21 business days"
  },
  {
    id: "planning",
    icon: TrendingUp,
    title: "Tax Planning",
    description: "Proactive tax strategies to help you plan ahead and stay compliant.",
    included: [
      "Quarterly estimated tax calculations",
      "Year-end tax planning sessions",
      "Retirement contribution strategies",
      "Income timing optimization",
      "Deduction planning",
      "Multi-year tax projections"
    ],
    whoItsFor: "High-income earners, business owners, and those seeking to optimize their tax situation.",
    turnaround: "Ongoing advisory relationship"
  },
  {
    id: "bookkeeping",
    icon: BookOpen,
    title: "Bookkeeping",
    description: "Monthly accounting services to keep your books accurate and up-to-date.",
    included: [
      "Bank & credit card reconciliation",
      "Monthly financial statements",
      "Accounts payable/receivable",
      "Expense categorization",
      "Financial reporting",
      "QuickBooks/Xero support"
    ],
    whoItsFor: "Small businesses needing reliable monthly bookkeeping support.",
    turnaround: "Monthly deliverables by the 15th"
  },
  {
    id: "notices",
    icon: FileWarning,
    title: "IRS & State Notice Support",
    description: "Expert assistance responding to tax notices and resolving issues.",
    included: [
      "Notice review and explanation",
      "Response preparation",
      "IRS/state correspondence",
      "Penalty abatement requests",
      "Payment plan arrangements",
      "Audit representation guidance"
    ],
    whoItsFor: "Anyone who has received a tax notice or needs resolution support.",
    turnaround: "Initial response within 48-72 hours"
  },
  {
    id: "ai-automation",
    icon: Bot,
    title: "AI Automation for Small Businesses",
    description: "Save time and improve follow-up with better lead intake, appointment scheduling, client reminders, CRM setup, review requests, and AI-assisted business workflows.",
    included: [
      "Website lead-capture systems",
      "Appointment scheduling & reminders",
      "Automated email & text follow-up",
      "CRM and lead-tracking setup",
      "Review-request automation",
      "Zapier, Make & n8n consulting"
    ],
    whoItsFor: "Small business owners who want to save time and never miss a lead.",
    turnaround: "Scoped per project after a strategy call"
  }
];

export default function Services() {
  const location = useLocation();
  const settings = useSettings();
  const PORTAL_URL = settings.portal_url;

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Our Services
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Comprehensive Tax & Accounting Services
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300">
              From individual tax preparation to full-service bookkeeping, we offer tailored solutions for individuals and businesses of all sizes.
            </p>
          </div>
        </div>
      </section>

      {/* Services Content with Sticky Sidebar */}
      <section className="bg-bone py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Sticky Sidebar */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-32 space-y-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-heading text-lg font-semibold text-navy mb-4">Quick Navigation</h3>
                  <nav className="space-y-2">
                    {services.map((service) => (
                      <a
                        key={service.id}
                        href={`#${service.id}`}
                        className="block font-body text-sm text-gray-600 hover:text-bronze transition-colors py-1"
                        data-testid={`nav-${service.id}`}
                      >
                        {service.title}
                      </a>
                    ))}
                  </nav>
                </div>

                <div className="bg-navy rounded-2xl p-6 text-white">
                  <h3 className="font-heading text-lg font-semibold mb-4">Ready to Start?</h3>
                  <div className="space-y-3">
                    <Button
                      asChild
                      className="w-full bg-bronze hover:bg-bronze/90 text-navy rounded-full font-medium"
                      data-testid="sidebar-portal-btn"
                    >
                      <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                        Secure Client Login
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full border-white text-white hover:bg-white hover:text-navy rounded-full font-medium"
                      data-testid="sidebar-book-btn"
                    >
                      <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                        Book Consultation
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Services List */}
            <div className="lg:col-span-3 space-y-12">
              {services.map((service, index) => (
                <Card 
                  key={service.id} 
                  id={service.id}
                  className="bg-white border-0 shadow-lg scroll-mt-32"
                  data-testid={`service-section-${service.id}`}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-bone flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-7 h-7 text-bronze" />
                      </div>
                      <div>
                        <CardTitle className="font-heading text-2xl text-navy mb-2">
                          {service.title}
                        </CardTitle>
                        <p className="font-body text-gray-600">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* What's Included */}
                    <div>
                      <h4 className="font-body font-semibold text-navy mb-3 flex items-center gap-2">
                        <Check className="w-5 h-5 text-bronze" />
                        What's Included
                      </h4>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {service.included.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 font-body text-gray-600">
                            <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-1" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Who It's For & Turnaround */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                      <div>
                        <h4 className="font-body font-semibold text-navy mb-2 flex items-center gap-2">
                          <Users className="w-5 h-5 text-bronze" />
                          Who It's For
                        </h4>
                        <p className="font-body text-gray-600 text-sm">
                          {service.whoItsFor}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-body font-semibold text-navy mb-2 flex items-center gap-2">
                          <Clock className="w-5 h-5 text-bronze" />
                          Typical Turnaround
                        </h4>
                        <p className="font-body text-gray-600 text-sm">
                          {service.turnaround}
                        </p>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="pt-4 flex flex-col sm:flex-row gap-3">
                      {service.id === "ai-automation" ? (
                        <Button
                          onClick={() => window.dispatchEvent(new CustomEvent("open-booking", { detail: { type: "AI Automation Strategy Call" } }))}
                          className="bg-navy hover:bg-gray-800 text-white rounded-full font-medium"
                          data-testid={`service-cta-${service.id}`}
                        >
                          Book an Automation Call
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            asChild
                            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full font-semibold"
                            data-testid={`service-cta-${service.id}`}
                          >
                            <Link to="/start">
                              Start Your Return
                              <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => window.dispatchEvent(new CustomEvent("open-booking"))}
                            className="border-navy text-navy hover:bg-navy hover:text-white rounded-full font-medium"
                            data-testid={`service-book-${service.id}`}
                          >
                            Book a Consultation
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-navy text-white noise-overlay py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
            Not Sure Which Service You Need?
          </h2>
          <p className="font-body text-gray-300 mb-8">
            Book a free consultation and we'll help you determine the best solution.
          </p>
          <Button
            asChild
            className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-6 text-lg font-medium"
            data-testid="services-cta-book"
          >
            <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
              Book Free Consultation
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}
